/**
 * True Woo — cookie consent manager
 *
 * Implements consent that meets the Australian Privacy Act / APP guidelines:
 *   - voluntary  : declining is one click, same prominence as accepting
 *   - informed   : each category is described and links to the Privacy Policy
 *   - specific   : per-category toggles, nothing bundled
 *   - current    : consent expires and is re-collected; bumping the policy
 *                  version in theme settings re-prompts everyone
 *   - withdrawable: the preferences dialog stays reachable from the footer
 *                  and from any link pointing at #cookie-preferences
 *
 * Nothing is opted in by default. Analytics and marketing stay off until the
 * visitor turns them on.
 *
 * Gating third-party tags:
 *   <script type="text/plain" data-consent-category="analytics" src="..."></script>
 *   <iframe data-consent-src="https://..." data-consent-category="marketing"></iframe>
 * Both are activated only once the matching category is granted.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cookie-consent]');
  if (!root) return;

  var STORAGE_KEY = 'truewoo_consent';
  var COOKIE_NAME = 'truewoo_consent';
  var CATEGORIES = ['functional', 'analytics', 'marketing'];

  var policyVersion = root.getAttribute('data-policy-version') || '1';
  var lifetimeMonths = parseInt(root.getAttribute('data-lifetime-months'), 10) || 12;

  var banner = root.querySelector('[data-cc-banner]');
  var modal = root.querySelector('[data-cc-modal]');
  var dialog = root.querySelector('[data-cc-dialog]');
  var recordNote = root.querySelector('[data-cc-record]');
  var lastFocused = null;

  /* ------------------------------------------------------------ storage */

  function denied() {
    return { functional: false, analytics: false, marketing: false };
  }

  function granted() {
    return { functional: true, analytics: true, marketing: true };
  }

  function readRecord() {
    var raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      raw = readCookie(COOKIE_NAME);
    }
    if (!raw) raw = readCookie(COOKIE_NAME);
    if (!raw) return null;

    var record;
    try {
      record = JSON.parse(decodeURIComponent(raw));
    } catch (e) {
      return null;
    }
    if (!record || !record.categories) return null;
    return record;
  }

  /** Consent must be current: same policy version and inside its lifetime. */
  function isCurrent(record) {
    if (!record) return false;
    if (String(record.version) !== String(policyVersion)) return false;

    var given = Date.parse(record.timestamp);
    if (isNaN(given)) return false;

    var expires = new Date(given);
    expires.setMonth(expires.getMonth() + lifetimeMonths);
    return Date.now() < expires.getTime();
  }

  function writeRecord(categories, method) {
    var record = {
      version: policyVersion,
      timestamp: new Date().toISOString(),
      method: method,
      categories: {
        necessary: true,
        functional: !!categories.functional,
        analytics: !!categories.analytics,
        marketing: !!categories.marketing
      }
    };

    var payload = JSON.stringify(record);
    try {
      window.localStorage.setItem(STORAGE_KEY, payload);
    } catch (e) {
      /* private browsing — the cookie mirror below still carries the record */
    }
    writeCookie(COOKIE_NAME, encodeURIComponent(payload), lifetimeMonths);
    return record;
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? match[1] : null;
  }

  function writeCookie(name, value, months) {
    var expires = new Date();
    expires.setMonth(expires.getMonth() + months);
    document.cookie =
      name +
      '=' +
      value +
      '; expires=' +
      expires.toUTCString() +
      '; path=/; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  /* ------------------------------------------------------------- apply */

  /** Hand the decision to Shopify so its own pixels honour it. */
  function syncShopify(categories) {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return;
        var api = window.Shopify.customerPrivacy;
        if (!api || typeof api.setTrackingConsent !== 'function') return;

        api.setTrackingConsent(
          {
            analytics: !!categories.analytics,
            marketing: !!categories.marketing,
            preferences: !!categories.functional,
            sale_of_data: !!categories.marketing
          },
          function () {}
        );
      }
    );
  }

  /** Activate any tags that were parked behind the categories now granted. */
  function releaseTags(categories) {
    CATEGORIES.forEach(function (category) {
      if (!categories[category]) return;

      var scripts = document.querySelectorAll(
        'script[type="text/plain"][data-consent-category="' + category + '"]'
      );
      Array.prototype.forEach.call(scripts, function (placeholder) {
        var script = document.createElement('script');
        Array.prototype.forEach.call(placeholder.attributes, function (attr) {
          if (attr.name === 'type') return;
          script.setAttribute(attr.name, attr.value);
        });
        script.type = 'text/javascript';
        script.text = placeholder.text;
        placeholder.parentNode.replaceChild(script, placeholder);
      });

      var frames = document.querySelectorAll(
        '[data-consent-src][data-consent-category="' + category + '"]'
      );
      Array.prototype.forEach.call(frames, function (frame) {
        frame.setAttribute('src', frame.getAttribute('data-consent-src'));
        frame.removeAttribute('data-consent-src');
      });
    });
  }

  function applyConsent(categories, record) {
    syncShopify(categories);
    releaseTags(categories);

    document.dispatchEvent(
      new CustomEvent('truewoo:consent', {
        detail: { categories: categories, record: record || null }
      })
    );
  }

  /* -------------------------------------------------------------- view */

  function showBanner() {
    if (!banner) return;
    banner.hidden = false;
    // Next frame, so the transition has a start value to animate from.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        banner.classList.add('is-visible');
      });
    });
  }

  function hideBanner() {
    if (!banner || banner.hidden) return;
    banner.classList.remove('is-visible');
    window.setTimeout(function () {
      banner.hidden = true;
    }, 350);
  }

  function setToggles(categories) {
    CATEGORIES.forEach(function (category) {
      var input = root.querySelector('[data-cc-toggle="' + category + '"]');
      if (input) input.checked = !!categories[category];
    });
  }

  function readToggles() {
    var categories = denied();
    CATEGORIES.forEach(function (category) {
      var input = root.querySelector('[data-cc-toggle="' + category + '"]');
      categories[category] = !!(input && input.checked);
    });
    return categories;
  }

  function renderRecord(record) {
    if (!recordNote) return;
    if (!record) {
      recordNote.hidden = true;
      return;
    }

    var when;
    try {
      when = new Date(record.timestamp).toLocaleString(document.documentElement.lang || 'en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    } catch (e) {
      when = record.timestamp;
    }

    recordNote.hidden = false;
    recordNote.textContent =
      (recordNote.getAttribute('data-cc-record-label') || 'Your choice was recorded on') +
      ' ' +
      when +
      ' (v' +
      record.version +
      ').';
  }

  /* ------------------------------------------------------------- modal */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(event) {
    if (event.key !== 'Tab' || !dialog) return;

    var items = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onModalKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeModal();
      return;
    }
    trapFocus(event);
  }

  function openModal() {
    if (!modal) return;

    lastFocused = document.activeElement;
    var record = readRecord();
    setToggles(record && record.categories ? record.categories : denied());
    renderRecord(isCurrent(record) ? record : null);

    modal.hidden = false;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        modal.classList.add('is-visible');
      });
    });

    document.addEventListener('keydown', onModalKeydown);
    document.documentElement.style.overflow = 'hidden';

    var focusTarget = dialog && dialog.querySelector(FOCUSABLE);
    if (focusTarget) focusTarget.focus();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;

    modal.classList.remove('is-visible');
    document.removeEventListener('keydown', onModalKeydown);
    document.documentElement.style.overflow = '';

    window.setTimeout(function () {
      modal.hidden = true;
    }, 250);

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  /* ------------------------------------------------------------ decide */

  function commit(categories, method) {
    var record = writeRecord(categories, method);
    applyConsent(categories, record);
    setToggles(categories);
    renderRecord(record);
    hideBanner();
    closeModal();
  }

  function acceptAll() {
    commit(granted(), 'accept_all');
  }

  function declineAll() {
    commit(denied(), 'reject_all');
  }

  function savePreferences() {
    commit(readToggles(), 'preferences');
  }

  /* ------------------------------------------------------------- wire */

  root.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-cc-action]');
    if (!trigger) return;

    event.preventDefault();
    switch (trigger.getAttribute('data-cc-action')) {
      case 'accept':
        acceptAll();
        break;
      case 'decline':
        declineAll();
        break;
      case 'save':
        savePreferences();
        break;
      case 'open':
        openModal();
        break;
      case 'close':
        closeModal();
        break;
    }
  });

  // Any link or button anywhere on the site can reopen preferences.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-cookie-preferences], a[href="#cookie-preferences"]');
    if (!trigger) return;
    event.preventDefault();
    openModal();
  });

  /* -------------------------------------------------------------- init */

  var stored = readRecord();

  if (isCurrent(stored)) {
    applyConsent(stored.categories, stored);
    setToggles(stored.categories);
  } else {
    // No valid consent on file: everything optional stays off, and a Global
    // Privacy Control signal is honoured immediately rather than waiting for
    // the visitor to act.
    applyConsent(denied(), null);
    setToggles(denied());
    if (navigator.globalPrivacyControl === true) {
      writeRecord(denied(), 'global_privacy_control');
    }
    showBanner();
  }

  window.TrueWooConsent = {
    open: openModal,
    close: closeModal,
    acceptAll: acceptAll,
    declineAll: declineAll,
    get: function () {
      var record = readRecord();
      return isCurrent(record) ? record : null;
    },
    has: function (category) {
      var record = readRecord();
      if (!isCurrent(record)) return false;
      return !!record.categories[category];
    }
  };
})();
